/**
 * Reachable project budget editor (PROJECT-BUDGET-FIELD-1).
 *
 * Lives on the live Budget tab. PATCHes /projects/:id (the settings
 * route does not persist budget). Hidden from guests (canSeeCost).
 * Edit gated by project.edit.
 */
import { useEffect, useState, type ReactElement } from 'react';
import { DollarSign } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button/Button';
import { Input } from '@/components/ui/input/Input';
import { useAuth } from '@/state/AuthContext';
import { canSeeCost } from '@/utils/access';
import { useEffectiveRole } from '@/utils/access/useEffectiveRole';
import { useProjectContext } from '@/features/projects/layout/ProjectPageLayout';
import { projectsApi } from '@/features/projects/projects.api';

/** decimal(10,2) on projects.budget — 8 digits left of the decimal. */
const MAX_BUDGET = 99_999_999.99;

function formatBudget(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(amount);
}

function budgetInputValue(budget: number | null | undefined): string {
  if (budget == null || Number.isNaN(Number(budget))) return '';
  return String(budget);
}

export function ProjectBudgetAmountField({
  onSaved,
}: {
  onSaved?: () => Promise<void> | void;
}): ReactElement | null {
  const { project, refresh, isReadOnly } = useProjectContext();
  const { can } = useEffectiveRole();
  const { user } = useAuth();
  const canEdit = can('project.edit') && !isReadOnly;
  const costVisible = canSeeCost(null, user?.platformRole ?? user?.role);

  const currency = project?.currency || 'USD';
  const [draft, setDraft] = useState(() => budgetInputValue(project?.budget));
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(budgetInputValue(project?.budget));
  }, [project?.id, project?.budget]);

  if (!costVisible || !project) return null;

  const current = project.budget != null ? Number(project.budget) : null;
  const hasBudget = current != null && current > 0;

  const save = async (): Promise<void> => {
    const parsed = Number(draft);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setError('Enter a budget greater than zero.');
      return;
    }
    if (parsed > MAX_BUDGET) {
      setError(`Budget cannot exceed ${formatBudget(MAX_BUDGET, currency)}.`);
      return;
    }
    if (hasBudget && parsed === current) {
      setError(null);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await projectsApi.updateProject(project.id, { budget: parsed });
      await refresh();
      await onSaved?.();
      toast.success('Budget saved');
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || 'Failed to save budget';
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="rounded-lg border border-slate-200 bg-white p-4 space-y-3"
      data-testid="project-budget-amount-field"
    >
      <div className="flex items-center gap-2">
        <DollarSign className="h-4 w-4 text-slate-500" aria-hidden />
        <h3 className="text-sm font-semibold text-slate-900">Project budget</h3>
      </div>

      {canEdit ? (
        <>
          <p className="text-sm text-slate-500">
            {hasBudget
              ? `Current budget: ${formatBudget(current, currency)}.`
              : 'No budget set.'}
          </p>
          <Input
            id="project-budget-amount"
            type="number"
            inputMode="decimal"
            min={0.01}
            max={MAX_BUDGET}
            step="0.01"
            label={`Budget (${currency})`}
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              if (error) setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void save();
              }
            }}
            error={error ?? undefined}
            data-testid="project-budget-amount-input"
          />
          <Button
            type="button"
            size="sm"
            onClick={() => void save()}
            loading={saving}
            disabled={saving}
            data-testid="project-budget-amount-save"
          >
            Save budget
          </Button>
        </>
      ) : (
        <p className="text-sm text-slate-700" data-testid="project-budget-amount-readonly">
          {hasBudget
            ? formatBudget(current, currency)
            : 'No budget set.'}
        </p>
      )}
    </div>
  );
}
